<script setup lang="ts">
import { computed, ref } from 'vue';
import { AgeSelector } from '@hfc/ui-core';
import DateSelector from '@/shared/infrastructure/components/DateSelector.vue';

interface EmitsInterface {
  (event: 'change', value: number | undefined): void,
}

const emits = defineEmits<EmitsInterface>();

const emitChange = (value: number | undefined): void => {
  model.value = value;
  emits('change', value);
};

const model = defineModel<number>();

const props = withDefaults(defineProps<{
  ageLabel?: string;
  dateLabel?: string;
  type: 'age' | 'date';
  buttonsOrientation?: 'horizontal' | 'vertical';
  size?: string;
  startAge?: number;
  endAge?: number;
  autoFocus?: boolean;
  useInputOnAge?: boolean;
  hasErrors?: boolean;
}>(), {
  ageLabel: 'Age',
  buttonsOrientation: 'vertical',
  dateLabel: 'Date',
  size: 'h-14'
});

const mode = ref(props.type);
const dob = computed<number|undefined>({
  get: () => model.value ?? undefined,
  set: (value: number | undefined) => {
    if (typeof value === 'number') {
      emitChange(value);
    }
  }
});

const buttonsClass = computed(() => props.buttonsOrientation === 'horizontal' ? horizontalButtonsClass : verticalButtonsClass);

const baseComponentClass = computed(() => props.buttonsOrientation === 'horizontal' ? 'flex-row' : 'flex-col');
const buttonsSectionClass = computed(() => props.buttonsOrientation === 'horizontal' ? 'order-2' : '');

const horizontalButtonsClass = {
  base: `flex w-14 ${props.size} justify-center items-center px-1 py-0.5 text-sm font-semibold`,
  active: 'bg-blue-600 text-white',
  inactive: 'bg-gray-100'
};

const verticalButtonsClass = {
  base: 'flex w-14 justify-center rounded-t-md px-1 py-0.5 text-sm font-semibold',
  active: 'bg-blue-600 text-white',
  inactive: 'bg-gray-100'
};
</script>
<template>
  <div class="flex w-full" :class="baseComponentClass">
    <div class="flex justify-end" :class="buttonsSectionClass">
      <button @click="mode = 'age'" :class="[buttonsClass.base, mode === 'age' ? buttonsClass.active : buttonsClass.inactive]" >
        {{ ageLabel }}
      </button>
      <button @click="mode = 'date'" :class="[buttonsClass.base, mode === 'date' ? buttonsClass.active : buttonsClass.inactive, buttonsOrientation === 'horizontal' ? 'rounded-r-md' : '']" >
        {{ dateLabel }}
      </button>
    </div>
    <AgeSelector v-if="mode === 'age'" v-model="dob" :css="buttonsOrientation === 'horizontal' ? 'no-border-right h-14' : 'no-border-top-right h-14'" :start-age="startAge" :end-age="endAge" :auto-focus="autoFocus" :use-text-input="useInputOnAge" @update:model-value="emitChange" />
    <DateSelector v-else v-model="dob" class="w-full" label="" id="dob-selector" :size="size" :has-errors="hasErrors" :input-class="buttonsOrientation === 'horizontal' ? 'border-r-0 rounded-r-none' : 'rounded-tr-none'" :auto-focus="autoFocus" @update:model-value="emitChange" />
  </div>
</template>
